import { Button, ButtonAsLink, ErrorText } from '@cluey/cluey-components';
import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import { ComponentProps, ReactNode, forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { api } from '../../api';
import { AccountDetails } from '../../api/types/account';
import ErrorBlock from '../../common/ErrorBlock';
import withHeadTag from '../../common/HeadComponent';
import { FieldWrapper } from '../../components/FieldWrapper';
import AccountPageLayout from '../../templates/AccountPageLayout/AccountPageLayout';
import { MOBILE_NUMBER_DUPLICATE_ERROR_MSG } from '../../util/constants';
import { formatError } from '../../util/errors';
import { InlineLoader } from '../hub/auth2/common';

export function mapErrorMessage(errorMessage: string) {
  if (errorMessage.includes('Mobile number already exists')) {
    return MOBILE_NUMBER_DUPLICATE_ERROR_MSG;
  }
  return errorMessage;
}

type ViewAccountDetailsProps = {
  details: AccountDetails;
  loading: false;
  edit: () => void;
};

type ViewAccountLoadingProps = {
  details: undefined;
  loading: true;
  edit: () => void;
};

type ViewAccountProps = ViewAccountDetailsProps | ViewAccountLoadingProps;

const SkeletonDiv = ({ loading, className, ...props }: { loading: boolean } & ComponentProps<'div'>) => (
  <div
    {...props}
    className={classNames('rounded-full leading-4', loading && 'animate-pulse bg-grey-2 text-transparent', className)}
  />
);

const ViewAccountDetails = (props: ViewAccountProps) => {
  let phone: ReactNode;
  if (props.loading) {
    phone = '0212341234'; // placeholder for skeleton width
  } else if (props.details.phone) {
    phone = props.details.phone;
  } else {
    phone = (
      <ButtonAsLink className="text-link" onClick={props.edit}>
        + Add a contact number
      </ButtonAsLink>
    );
  }

  return (
    <div className="container flex flex-col rounded-3xl border border-gray-100 bg-white px-5 py-6 md:px-10 md:py-10">
      <div className="mb-6 md:mb-12">
        <h4 className="font-[Merriweather] text-2xl font-bold">Contact details</h4>
      </div>
      <div className="mb-10 flex flex-col gap-y-4 font-[Poppins] md:mb-12">
        <div className="flex flex-col gap-y-2 overflow-x-auto overflow-y-hidden pb-2">
          <SkeletonDiv className="font-semibold" loading={props.loading}>
            First name
          </SkeletonDiv>
          <SkeletonDiv loading={props.loading}>{!props.loading ? props.details.firstName : 'Joseph'}</SkeletonDiv>
        </div>
        <div className="flex flex-col gap-y-2 overflow-x-auto overflow-y-hidden pb-2">
          <SkeletonDiv className="font-semibold" loading={props.loading}>
            Last name
          </SkeletonDiv>
          <SkeletonDiv loading={props.loading}>{!props.loading ? props.details.lastName : 'Smith'}</SkeletonDiv>
        </div>
        <div className="flex flex-col gap-y-2 overflow-x-auto overflow-y-hidden pb-2">
          <SkeletonDiv className="font-semibold" loading={props.loading}>
            Email
          </SkeletonDiv>
          <SkeletonDiv loading={props.loading}>
            {!props.loading ? props.details.email : 'jsmith@example.com'}
          </SkeletonDiv>
        </div>
        <div className="flex flex-col gap-y-2 overflow-x-auto overflow-y-hidden pb-2">
          <SkeletonDiv className="font-semibold" loading={props.loading}>
            Preferred contact number
          </SkeletonDiv>
          <SkeletonDiv loading={props.loading}>{!props.loading ? phone : '0212341234'}</SkeletonDiv>
        </div>
      </div>
      <div className="flex flex-row overflow-x-auto overflow-y-hidden pb-2">
        <Button
          size="lg"
          disabled={props.loading}
          className={classNames(
            props.loading ? 'animate-pulse !text-transparent' : '',
            'flex-grow truncate md:flex-grow-0'
          )}
          onClick={props.edit}
        >
          Update contact details
        </Button>
      </div>
    </div>
  );
};

const EditableAccountDetails = z.object({
  firstName: z
    .string()
    .min(1, 'Please enter a valid first name')
    .regex(/^[a-zA-Z\s0-9_\-']+$/, "Please enter only letters, numbers, spaces and any of these symbols: _-'."),
  lastName: z
    .string()
    .min(1, 'Please enter a valid last name')
    .regex(/^[a-zA-Z\s0-9_\-']+$/, "Please enter only letters, numbers, spaces and any of these symbols: _-'."),
  email: z.string().email('Please enter a valid email address'),
  phone: z
    .string()
    .transform((val) => val.replace(/[ +()]/g, ''))
    .pipe(
      z
        .string()
        .min(7, 'Phone number must be 7 digits or more')
        .max(12, 'Phone number must be 12 digits or less')
        .regex(/^[0-9]+$/, 'Please enter a valid phone number')
    ),
});
type EditableAccountDetails = z.infer<typeof EditableAccountDetails>;

// We need to update cluey-components to properly support react-hook-form
const FormInput = forwardRef<HTMLInputElement, ComponentProps<'input'> & { error?: boolean }>(function FormInput(
  { className, error, ...props },
  ref
) {
  return (
    <input
      ref={ref}
      {...props}
      className={classNames(
        'rounded border px-2 py-2 focus:outline-none focus:ring focus:ring-blue-4/40',
        error ? 'border-red-5' : 'border-grey-3 focus:border-blue-4',
        className
      )}
    />
  );
});

const EditAccountDetails = ({ details, view }: { details: AccountDetails; view: () => void }) => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { isSubmitting, isDirty, errors },
    reset,
  } = useForm<EditableAccountDetails>({
    resolver: zodResolver(EditableAccountDetails),
    defaultValues: {
      firstName: details.firstName,
      lastName: details.lastName,
      email: details.email,
      phone: details.phone || '',
    },
  });

  const { mutateAsync: mutateCustomer } = api.account.update.useMutation({
    onError: (error) => {
      reset(undefined, { keepDirtyValues: true, keepErrors: true, keepDirty: true });
      setError('root', {
        message: mapErrorMessage(formatError(error)),
      });
    },
  });

  const onSubmit = handleSubmit(async (data: EditableAccountDetails) => {
    if (!isDirty) {
      view();
      return;
    }

    await mutateCustomer({
      customerType: details.userType,
      customerNumber: details.customerNumber,
      firstname: data.firstName,
      lastname: data.lastName,
      mobilePhone: data.phone,
      email: data.email,
    });

    const currData = api.account.details.getQueryData();
    api.account.details.setQueryData(undefined, {
      ...currData,
      ...data,
    });
    api.account.details.invalidateQueries();
    view();
  });

  return (
    <div className="container flex flex-col rounded-3xl border border-gray-100 bg-white px-5 py-6 md:px-10 md:py-10">
      <h4 className="mb-6 font-[Merriweather] text-2xl font-bold md:mb-12">Update contact details</h4>
      <div className="w-full">
        <form onSubmit={onSubmit} className="flex w-full flex-col items-start gap-4">
          <FieldWrapper
            label="First name"
            id="account-details-first-name"
            error={errors.firstName?.message}
            className="w-full"
          >
            <FormInput
              {...register('firstName')}
              disabled={isSubmitting}
              id="account-details-first-name"
              className="!w-full rounded-full p-3"
            />
          </FieldWrapper>
          <FieldWrapper
            label="Last name"
            id="account-details-last-name"
            error={errors.lastName?.message}
            className="w-full"
          >
            <FormInput
              {...register('lastName')}
              disabled={isSubmitting}
              id="account-details-last-name"
              className="rounded-full p-3"
            />
          </FieldWrapper>
          <FieldWrapper
            label="Email address"
            id="account-details-email"
            error={errors.email?.message}
            className="w-full"
          >
            <FormInput
              {...register('email')}
              disabled={isSubmitting}
              id="account-details-email"
              className="rounded-full p-3"
            />
          </FieldWrapper>
          <div className="flex w-full gap-8">
            <FieldWrapper
              label="Preferred contact number"
              id="account-details-phone"
              error={errors.phone?.message}
              className="w-full"
            >
              <FormInput
                {...register('phone')}
                disabled={isSubmitting}
                id="account-details-phone"
                error={!!errors.phone?.message || !!errors.root?.message}
                className="rounded-full p-3"
              />
              <span className="text-xs text-gray-400">Include any applicable area code</span>
            </FieldWrapper>
          </div>
          {errors.root && <ErrorText className="mt-4">{errors.root.message}</ErrorText>}
          <div className="flex w-full flex-col gap-3 md:flex-row">
            <Button type="submit" disabled={isSubmitting} className="min-w-[160px]">
              {isSubmitting ? <InlineLoader /> : 'Save'}
            </Button>
            <Button appearance="reverse" onClick={view} className="min-w-[160px] text-base normal-case">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AccountDetailsPage = () => {
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  const { data: accountDetails, isLoading, error } = api.account.details.useQuery();

  return (
    <AccountPageLayout>
      <section className="mb-10 pb-10 lg:mb-0 lg:pb-0">
        {error && <ErrorBlock errorMsg={formatError(error)} />}
        {isLoading ? (
          <ViewAccountDetails loading={true} details={undefined} edit={() => setMode('edit')} />
        ) : mode === 'view' ? (
          <ViewAccountDetails loading={false} details={accountDetails} edit={() => setMode('edit')} />
        ) : (
          <EditAccountDetails details={accountDetails} view={() => setMode('view')} />
        )}
        <div className="mb-10 pb-10" />
      </section>
    </AccountPageLayout>
  );
};

export default withHeadTag(AccountDetailsPage);
